/**
 * 基于 screenfull 的全屏封装（与 Element Plus 生态常用方案一致，自动处理各浏览器前缀）。
 *
 * 与考试守卫一同从 `@/utils/examGuard` 导出；调用顺序由业务页面控制。
 *
 * 说明：多数浏览器要求 requestFullscreen 处于「用户激活」栈内。
 * 在登录按钮等点击回调里**最先**同步调用 tryEnterFullscreenFromUserGesture()，再执行 await，
 * 可提高进入考试页时已处于全屏的成功率。
 */
import screenfull from "screenfull";

function canUseScreenfull() {
  return Boolean(screenfull.isEnabled && typeof screenfull.request === "function");
}

/**
 * 在用户点击的同一同步阶段触发全屏（不 await），避免后续 await 消耗用户激活后无法再进全屏。
 * 应在「登录」等按钮 handler 的**第一行**调用。
 */
export function tryEnterFullscreenFromUserGesture() {
  if (!canUseScreenfull()) {
    return;
  }

  void screenfull.request().catch(() => {});
}

/**
 * @param {Element} [el=document.documentElement]
 * @returns {Promise<boolean>} 调用结束后是否处于全屏状态
 */
export async function requestFullscreen(el = document.documentElement) {
  if (!canUseScreenfull()) {
    console.warn("当前环境不支持全屏 API");
    return false;
  }

  try {
    await screenfull.request(el);
    return Boolean(screenfull.isFullscreen);
  } catch (err) {
    console.warn("进入全屏失败", err);
    return false;
  }
}

/** @returns {boolean} */
export function isFullscreen() {
  return Boolean(canUseScreenfull() && screenfull.isFullscreen);
}

/** 离开考试页时恢复窗口，忽略未在全屏时的报错 */
export async function exitFullscreenSafe() {
  if (!canUseScreenfull()) {
    return;
  }

  try {
    if (screenfull.isFullscreen) {
      await screenfull.exit();
    }
  } catch (err) {
    console.warn("退出全屏", err);
  }
}
